import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { LoadingSpinner } from '../../components/general/LoadingSpinner';
import ViewToggle from '../../components/data_hub/ViewToggle';
import DataTable from '../../components/data_hub/DataTable';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';

const ChargingStationMapView = () => {
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');
  const { isLoaded, loadError } = useGoogleMaps();
  
  const [view, setView] = useState('map');
  const [doeStations, setDoeStations] = useState([]);
  const [calstartStations, setCalstartStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Map state
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const infoWindowRef = useRef(null);
  const projectionReadyRef = useRef(false);
  const renderRetryListenerAttachedRef = useRef(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [dataSource, setDataSource] = useState('all'); // 'all', 'doe', 'calstart'
  const [selectedStatus, setSelectedStatus] = useState(''); // For CalSTART
  const [availableStates, setAvailableStates] = useState([]);

  // Defined before the useEffects that depend on them.
  // useCallback stabilises the function identity so useEffect deps don't
  // fire on every render, and removes the "call before initialization" warning.

  const fetchStations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [doeResponse, calstartResponse] = await Promise.all([
        axios.get(API_ENDPOINTS.getChargingStationsDOE, {
          headers: { Authorization: `Token ${authToken}` },
          params: { format: 'json' },
        }),
        axios.get(API_ENDPOINTS.getChargingStationsCalSTART, {
          headers: { Authorization: `Token ${authToken}` },
        }),
      ]);

      const doeData = doeResponse.data.stations || [];
      const calstartData = calstartResponse.data || [];

      setDoeStations(doeData);
      setCalstartStations(calstartData);

      const doeStates = doeData.map(s => s.state).filter(Boolean);
      const calstartStates = calstartData.map(s => s.addr?.split(',').pop()?.trim()).filter(Boolean);
      const states = [...new Set([...doeStates, ...calstartStates])].sort();
      setAvailableStates(states);
    } catch (err) {
      console.error('Error fetching charging stations:', err);
      setError('Failed to load charging stations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Depends only on filter state; stable identity lets renderMarkers depend on it safely.
  const getFilteredStations = useCallback(() => {
    let combined = [];

    if (dataSource === 'all' || dataSource === 'doe') {
      combined = combined.concat(
        doeStations.map(s => ({
          ...s,
          source: 'doe',
          name: s.station_name,
          address: s.street_address,
          lat: s.latitude,
          lng: s.longitude,
        }))
      );
    }

    if (dataSource === 'all' || dataSource === 'calstart') {
      combined = combined.concat(
        calstartStations.map(s => ({
          ...s,
          source: 'calstart',
          address: s.addr,
        }))
      );
    }

    return combined.filter(station => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          station.name?.toLowerCase().includes(searchLower) ||
          station.address?.toLowerCase().includes(searchLower) ||
          station.city?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (selectedState) {
        const stationState = station.state || station.addr?.split(',').pop()?.trim();
        if (stationState !== selectedState) return false;
      }

      if (selectedStatus && station.source === 'calstart') {
        if (station.status !== selectedStatus) return false;
      }

      return true;
    });
  }, [doeStations, calstartStations, dataSource, selectedState, searchTerm, selectedStatus]);

  const renderMarkers = useCallback(() => {
    if (!mapInstanceRef.current) return;
    if (view !== 'map') return;

    // Guard: MarkerClusterer GridAlgorithm requires projection to compute pixel bounds.
    const map = mapInstanceRef.current;
    const projection = typeof map.getProjection === 'function' ? map.getProjection() : null;
    if (!projectionReadyRef.current && !projection) {
      if (!renderRetryListenerAttachedRef.current && window.google?.maps?.event?.addListenerOnce) {
        renderRetryListenerAttachedRef.current = true;
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          renderRetryListenerAttachedRef.current = false;
          projectionReadyRef.current = true;
          renderMarkers();
        });
      }
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    if (markerClustererRef.current) {
      try {
        markerClustererRef.current.setMap(null);
        markerClustererRef.current.clearMarkers();
      } catch (e) {
        console.warn('MarkerClusterer teardown failed, recreating.', e);
      } finally {
        markerClustererRef.current = null;
      }
    }

    const filteredStations = getFilteredStations();
    const markers = [];

    filteredStations.forEach(station => {
      const lon = station.lon ?? station.lng;
      if (!station.lat || lon == null || !Number.isFinite(parseFloat(lon))) return;

      const isDoe = station.source === 'doe';
      const marker = new window.google.maps.Marker({
        position: { lat: parseFloat(station.lat), lng: parseFloat(lon) },
        map: mapInstanceRef.current,
        title: station.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: isDoe ? '#3B82F6' : '#8B5CF6',
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });

      marker.addListener('click', () => {
        handleStationClick(station, marker);
      });

      markers.push(marker);
      markersRef.current.push(marker);
    });

    if (markers.length > 0) {
      markerClustererRef.current = new MarkerClusterer({
        map,
        markers,
        algorithm: new GridAlgorithm({ maxZoom: 15 }),
      });
    }
  }, [view, getFilteredStations]);

  const initializeMap = useCallback(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    if (window.google?.maps?.event?.addListenerOnce) {
      window.google.maps.event.addListenerOnce(map, 'projection_changed', () => {
        projectionReadyRef.current = true;
        if (view === 'map') {
          renderMarkers();
        }
      });
    }
  }, [view, renderMarkers]);

  useEffect(() => {
    if (isLoaded) {
      fetchStations();
    }
  }, [isLoaded, fetchStations]);

  useEffect(() => {
    if (isLoaded && view === 'map' && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [isLoaded, view, initializeMap]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (view !== 'map') return;
    renderMarkers();
  }, [view, doeStations, calstartStations, dataSource, selectedState, searchTerm, selectedStatus, renderMarkers]);

  const handleStationClick = useCallback((station, marker) => {
    setSelectedStation(station);
    
    const isDoe = station.source === 'doe';
    let contentString = `
      <div style="padding: 10px; max-width: 300px;">
        <h3 style="font-size: 16px; font-weight: bold; margin-bottom: 8px; color: ${isDoe ? '#3B82F6' : '#8B5CF6'}">
          ${station.name}
        </h3>
        <p style="font-size: 12px; color: #6B7280; margin-bottom: 4px;">
          <strong>Source:</strong> ${isDoe ? 'DOE' : 'CalSTART'}
        </p>
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Address:</strong> ${station.address || 'N/A'}
        </p>
    `;

    if (isDoe) {
      contentString += `
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Network:</strong> ${station.ev_network || 'N/A'}
        </p>
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Level 2:</strong> ${station.ev_level2_evse_num || 0} ports
        </p>
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>DC Fast:</strong> ${station.ev_dc_fast_count || 0} ports
        </p>
      `;
    } else {
      contentString += `
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Status:</strong> ${station.status || 'N/A'}
        </p>
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Max Power:</strong> ${station.max_power ? `${station.max_power} kW` : 'N/A'}
        </p>
        <p style="font-size: 12px; color: #374151; margin-bottom: 4px;">
          <strong>Stall Count:</strong> ${station.stall_count || 'N/A'}
        </p>
      `;
    }

    contentString += '</div>';

    infoWindowRef.current.setContent(contentString);
    infoWindowRef.current.open(mapInstanceRef.current, marker);
  }, []);

  const handleReset = () => {
    setSearchTerm('');
    setSelectedState('');
    setDataSource('all');
    setSelectedStatus('');
  };

  const tableColumns = [
    { 
      key: 'source', 
      label: 'Source', 
      format: 'string',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value === 'doe' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
        }`}>
          {value === 'doe' ? 'DOE' : 'CalSTART'}
        </span>
      )
    },
    { key: 'name', label: 'Station Name', format: 'string' },
    { key: 'address', label: 'Address', format: 'string' },
    { key: 'city', label: 'City', format: 'string' },
    { key: 'state', label: 'State', format: 'string' },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/data')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaArrowLeft />
                  <span>Back to DataHub</span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 font-ui">EV Charging Stations</h1>
                  <p className="text-sm text-gray-600">DOE and CalSTART charging infrastructure data</p>
                </div>
              </div>
              <ViewToggle view={view} onViewChange={setView} />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>

              <select
                value={dataSource}
                onChange={(e) => setDataSource(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sources</option>
                <option value="doe">DOE Only</option>
                <option value="calstart">CalSTART Only</option>
              </select>

              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All States</option>
                {availableStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                disabled={dataSource === 'doe'}
              >
                <option value="">All Status</option>
                <option value="Open">Open</option>
                <option value="In Development">In Development</option>
                <option value="Planned">Planned</option>
              </select>

              <div className="col-span-2">
                <button
                  onClick={handleReset}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-gray-700">DOE Stations</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                <span className="text-gray-700">CalSTART Stations</span>
              </div>
            </div>
          </div>

          {/* Content */}
          {view === 'map' ? (
            <div className="flex-1 relative">
              <div ref={mapRef} className="w-full h-full" />

              {/* Google Maps script error */}
              {loadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-700 font-medium">
                      Failed to load Google Maps: {loadError.message}
                    </p>
                  </div>
                </div>
              )}

              {/* Data loading overlay (keep basemap visible) */}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <LoadingSpinner message="Loading charging stations..." />
                </div>
              )}

              {/* Data error overlay (keep basemap visible) */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-600 font-medium">{error}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingSpinner message="Loading charging stations..." />
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-600 font-medium">{error}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md mb-4">
                  <div className="p-4 border-b border-gray-200">
                    <p className="text-gray-600">
                      Showing {getFilteredStations().length} stations
                    </p>
                  </div>
                  <DataTable
                    columns={tableColumns}
                    data={getFilteredStations()}
                    onRowClick={(row) => {
                      const rowLon = row.lon ?? row.lng;
                      if (view === 'table' && row.lat && rowLon != null) {
                        setView('map');
                        setTimeout(() => {
                          if (mapInstanceRef.current) {
                            mapInstanceRef.current.setCenter({
                              lat: parseFloat(row.lat),
                              lng: parseFloat(rowLon)
                            });
                            mapInstanceRef.current.setZoom(15);
                          }
                        }, 100);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
    </div>
  );
};

export default ChargingStationMapView;
